import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Renders a carousel of recently viewed products on the product page.
 *
 * Recently viewed products are tracked client-side in localStorage (see
 * `RecentlyViewed`). This component reads that list, excludes the current
 * product, fetches matching product cards via the Section Rendering API on the
 * `/search` route (`q=id:… OR id:…`), reorders the results most-recent-first,
 * injects them, and reveals the section. When there are no other recently
 * viewed products the section stays hidden.
 */
class RecentlyViewedProducts extends Component {
  /** @type {boolean} */
  #loaded = false;

  connectedCallback() {
    super.connectedCallback();
    // Load immediately rather than via IntersectionObserver: the section starts
    // with `.hidden` (display:none), which has no layout box, so an observer's
    // isIntersecting would never fire — it stays hidden forever. The fetch is
    // cheap and the script is already loaded at fetchpriority="low".
    this.#load();
  }

  /**
   * Fetch, reorder and reveal the recently viewed products.
   */
  async #load() {
    if (this.#loaded) return;
    this.#loaded = true;

    const { sectionId, currentProductId } = this.dataset;
    if (!sectionId) return;

    // Exclude the product currently being viewed.
    const ids = RecentlyViewed.getProducts().filter((id) => String(id) !== String(currentProductId));
    if (ids.length === 0) return; // Nothing to show — stay hidden.

    try {
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set('q', ids.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
      url.searchParams.set('resources[type]', 'product');

      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
      // The standalone /search render contains exactly one <recently-viewed-products>.
      // Match by tag, not id: the standalone render's id is derived from the section
      // file handle and won't equal this element's template-instance id.
      const root = new DOMParser().parseFromString(html, 'text/html').querySelector('recently-viewed-products');

      if (!root) return;

      // Bail (stay hidden) if the search resolved no products.
      if (!root.querySelector('product-card, .resource-list__item')) return;

      this.#reorderByRecency(root, ids);

      // The standalone /search render has no template-instance blocks, so its
      // heading area is empty. Preserve the configured heading rendered by the
      // live instance (this element already contains it, just hidden).
      const heading = this.querySelector('.section-resource-list__content')?.innerHTML;

      this.innerHTML = root.innerHTML;

      const headingTarget = this.querySelector('.section-resource-list__content');
      if (heading && headingTarget) headingTarget.innerHTML = heading;

      // `product-card` is rendered without a block, so every card shares
      // id="product-card-". Make them unique to keep ids valid.
      for (const card of this.querySelectorAll('product-card[id="product-card-"]')) {
        const productId = card.getAttribute('data-product-id');
        if (productId) card.id = `product-card-rv-${productId}`;
      }

      this.classList.remove('hidden');
    } catch (error) {
      console.error('Recently viewed products error:', error instanceof Error ? error.message : error);
    }
  }

  /**
   * Reorder rendered cards to match the recency order from localStorage.
   * Search relevance order is not recency, so we sort the slides (carousel) or
   * items (grid) by their product id's position in `orderedIds`.
   * @param {Element} root - The parsed `<recently-viewed-products>` element.
   * @param {string[]} orderedIds - Product ids, most-recent first.
   */
  #reorderByRecency(root, orderedIds) {
    const rank = new Map(orderedIds.map((id, index) => [String(id), index]));

    /** @param {Element} node */
    const rankOf = (node) => {
      const id = node.querySelector('[data-product-id]')?.getAttribute('data-product-id');
      return id != null && rank.has(id) ? /** @type {number} */ (rank.get(id)) : Number.MAX_SAFE_INTEGER;
    };

    const slides = Array.from(root.querySelectorAll('slideshow-slide'));
    if (slides.length > 1) {
      const parent = slides[0]?.parentElement;
      if (!parent) return;

      slides.sort((a, b) => rankOf(a) - rankOf(b));
      slides.forEach((slide, index) => {
        // Restore the index-derived attributes for the new position.
        slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
        slide.toggleAttribute('inert', index !== 0);
        const style = slide.getAttribute('style');
        if (style) slide.setAttribute('style', style.replace(/--slide-\d+/g, `--slide-${index}`));
        parent.appendChild(slide);
      });
      return;
    }

    // Grid layout fallback.
    const items = Array.from(root.querySelectorAll('.resource-list__item'));
    if (items.length > 1) {
      const parent = items[0]?.parentElement;
      if (!parent) return;

      items.sort((a, b) => rankOf(a) - rankOf(b));
      for (const item of items) parent.appendChild(item);
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
